import { type FC } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface EngagementEmptyStateProps {
  message: string;
}

export const EngagementEmptyState: FC<EngagementEmptyStateProps> = ({ message }) => (
  <div className="engagement-overview-empty" role="status">
    <p>{message}</p>
  </div>
);

export type LegendChipItem = {
  key: string;
  label: string;
  color: string;
};

interface ChartLegendChipsProps {
  items: ReadonlyArray<LegendChipItem>;
  activeKeys: string[];
  onToggle: (_key: string) => void;
  ariaLabel: string;
}

export const ChartLegendChips: FC<ChartLegendChipsProps> = ({
  items,
  activeKeys,
  onToggle,
  ariaLabel,
}) => (
  <fieldset className="engagement-overview-legend-chips" aria-label={ariaLabel}>
    <legend className="engagement-overview-visually-hidden">{ariaLabel}</legend>
    {items.map(item => {
      const active = activeKeys.includes(item.key);
      return (
        <button
          key={item.key}
          type="button"
          className={
            active
              ? "engagement-overview-legend-chips__item engagement-overview-legend-chips__item--active"
              : "engagement-overview-legend-chips__item"
          }
          onClick={() => onToggle(item.key)}
        >
          <span
            className={`engagement-overview-legend-chips__dot engagement-overview-legend-chips__dot--${item.key}`}
            aria-hidden
          />
          {item.label}
        </button>
      );
    })}
  </fieldset>
);

interface EngagementSectionPaginationProps {
  currentPage: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  ariaLabel: string;
}

export const EngagementSectionPagination: FC<EngagementSectionPaginationProps> = ({
  currentPage,
  totalPages,
  onPrev,
  onNext,
  ariaLabel,
}) => {
  const safeTotal = Math.max(1, totalPages);
  const safeCurrent = Math.min(Math.max(1, currentPage), safeTotal);

  return (
    <nav className="engagement-overview-pagination" aria-label={ariaLabel}>
      <button
        type="button"
        className="engagement-overview-pagination__btn"
        onClick={onPrev}
        disabled={safeCurrent <= 1}
        aria-label="Previous page"
      >
        <ChevronLeft size={16} aria-hidden />
      </button>
      <span className="engagement-overview-visually-hidden engagement-overview-pagination__label">
        {`Page ${safeCurrent} of ${safeTotal}`}
      </span>
      <button
        type="button"
        className="engagement-overview-pagination__btn"
        onClick={onNext}
        disabled={safeCurrent >= safeTotal}
        aria-label="Next page"
      >
        <ChevronRight size={16} aria-hidden />
      </button>
    </nav>
  );
};
